<template>
  <div class="daily-aggregation-step">
    <!-- カテゴリ見出し + 日計ジョブ一括実行ボタン（同一ライン） -->
    <div class="category-bar d-flex align-center mb-4">
      <h2 class="category-heading">
        {{ categoryLabel }}
      </h2>
      <v-spacer />
      <v-btn
        v-if="!isLoading && aggregationsData.length > 0"
        color="primary"
        :ripple="false"
        :loading="isBulkEnqueueing"
        :disabled="!canBulkEnqueue"
        @click="onBulkEnqueue"
      >
        <v-icon left>
          {{ icons.mdiSync }}
        </v-icon>
        日計ジョブ一括実行
      </v-btn>
    </div>

    <v-skeleton-loader
      v-if="isLoading"
      type="table"
    />

    <template v-else>
      <!-- 集計表が無い場合 -->
      <v-alert
        v-if="aggregationsData.length === 0"
        type="info"
        text
        dense
      >
        {{ categoryLabel }}の集計表が設定されていません
      </v-alert>

      <template v-else>
        <!-- 未保存サマリー -->
        <v-alert
          v-if="unsavedCount > 0"
          color="warning"
          text
          class="unsaved-summary mb-4"
        >
          <div class="d-flex align-center">
            <v-icon
              color="warning"
              class="mr-3"
            >
              {{ icons.mdiAlert }}
            </v-icon>
            <span class="font-weight-bold warning--text">
              {{ categoryLabel }}に未保存が {{ unsavedCount }} 件あります
            </span>
          </div>
        </v-alert>

        <!-- スプレッドシート -->
        <v-card
          outlined
          class="spread"
        >
          <!-- シートのツールバー -->
          <div class="sheet-bar d-flex align-center px-4 py-2">
            <span class="sheet-title font-weight-bold mr-3">{{ activeSheet.name }}</span>
            <sheet-status-chip :status="activeStatus" />
            <v-spacer />
            <span
              v-if="activeTimestamp"
              class="sheet-meta text--disabled mr-3"
            >
              最終集計日時: {{ activeTimestamp }}
            </span>

            <!-- 保存（未保存があり、編集中・保存処理中でないときのみ有効） -->
            <v-btn
              color="primary"
              small
              class="mr-2"
              :ripple="false"
              :disabled="!canSave"
              @click="$emit('save', activeSheet.id)"
            >
              保存
            </v-btn>

            <!-- 再集計（集計中以外で有効） -->
            <v-btn
              color="secondary"
              small
              :ripple="false"
              :disabled="activeStatus === 'progress'"
              :loading="activeStatus === 'progress'"
              @click="$emit('reload', activeSheet.id)"
            >
              <v-icon
                left
                small
              >
                {{ icons.mdiReload }}
              </v-icon>
              再集計
            </v-btn>

            <!-- ⋮ メニュー -->
            <v-menu
              bottom
              left
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="ml-1"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>{{ icons.mdiDotsVertical }}</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-subheader>操作</v-subheader>

                <!-- 適用ユーザーの変更（ユーザー集計のときのみ） -->
                <v-list-item
                  v-if="isUserAggregation"
                  @click="usersDialogOpen = true"
                >
                  <v-list-item-title>適用ユーザーの変更</v-list-item-title>
                </v-list-item>

                <!-- クリップボードにコピー -->
                <v-list-item @click="copyToClipboard">
                  <v-list-item-title>クリップボードにコピー</v-list-item-title>
                </v-list-item>

                <v-list-item @click="$emit('refresh', activeSheet.id)">
                  <v-list-item-title class="error--text">
                    リセットして再集計
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <!-- シート本体 -->
          <div class="sheet-body">
            <!-- エラー -->
            <div
              v-if="activeStatus === 'error'"
              class="pa-6"
            >
              <v-alert
                type="error"
                text
                dense
              >
                【集計に失敗しました】{{ activeErrorMessage || '原因不明のエラー' }}<br />
                再集計してもエラーが続く場合はサポートにお問い合わせください。
              </v-alert>
            </div>

            <!-- 集計中 -->
            <div
              v-else-if="activeStatus === 'progress'"
              class="pa-6"
            >
              <p class="text--secondary mb-2">
                集計を実行しています。しばらくお待ちください。
              </p>
              <v-progress-linear
                indeterminate
                color="primary"
                rounded
              />
            </div>

            <!-- スプレッドシート形式の罫線グリッド -->
            <!-- パディングは付けない（name 列の sticky 固定を左端ぴったりに -->
            <!-- 付けるため、また上下の余分な余白も出さないため）。 -->
            <div
              v-else-if="activeAggregation"
              class="grid-wrap"
            >
              <aggregation-grid
                :key="`sheet-grid-${activeSheet.id}`"
                ref="gridRef"
                :aggregation="activeAggregation"
                :loading="!!loadingStatuses[activeSheet.id]"
                @save="$emit('save-cell', $event)"
                @busy-change="isGridBusy = $event"
              />
            </div>

            <!-- 集計データ未取得（ジョブ未実行） -->
            <div
              v-else
              class="pa-6"
            >
              <v-alert
                type="warning"
                text
                dense
              >
                まだ集計が実行されていません。「再集計」を押して集計を実行してください。
              </v-alert>
            </div>
          </div>

          <!-- シートタブ（下部・Excel風） -->
          <div class="sheet-tabs d-flex px-2 py-1">
            <button
              v-for="(sheet, idx) in aggregationsData"
              :key="`sheet-tab-${sheet.attributes.id}`"
              type="button"
              class="sheet-tab"
              :class="{ active: idx === activeIndex }"
              @click="activeIndex = idx"
            >
              <v-icon
                small
                :color="idx === activeIndex ? 'white' : statusColor(sheet.attributes.id)"
                class="mr-1"
              >
                {{ statusIcon(sheet.attributes.id) }}
              </v-icon>
              {{ sheet.attributes.name }}
            </button>
          </div>
        </v-card>

        <!-- 適用ユーザーの変更ダイアログ -->
        <aggregation-users-dialog
          v-if="isUserAggregation"
          v-model="usersDialogOpen"
          :aggregation-users="activeAggregationUsers"
          :available-users="activeAvailableUsers"
          @fetch-available-users="onFetchAvailableUsers"
          @create="onCreateUser"
          @destroy="onDestroyUser"
        />
      </template>
    </template>
  </div>
</template>

<script>
import {
  ref, computed, watch, getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiReload,
  mdiSync,
  mdiDotsVertical,
  mdiAlert,
  mdiAlertCircle,
  mdiTimerSand,
  mdiCheckCircle,
} from '@mdi/js'
import AggregationGrid from './AggregationGrid.vue'
import AggregationUsersDialog from './AggregationUsersDialog.vue'
import SheetStatusChip from './SheetStatusChip.vue'

const STATUS_ICON = {
  error: mdiAlertCircle,
  unsaved: mdiAlert,
  progress: mdiTimerSand,
  saved: mdiCheckCircle,
}
const STATUS_COLOR = {
  error: 'error',
  unsaved: 'warning',
  progress: 'secondary',
  saved: 'success',
}

export default {
  name: 'DailyAggregationStep',
  components: {
    AggregationGrid,
    AggregationUsersDialog,
    SheetStatusChip,
  },
  props: {
    // 表示対象カテゴリの集計表データ（aggregationsData の該当タイプ分）
    aggregationsData: {
      type: Array,
      default: () => [],
    },
    // 集計表ID -> 集計表（結果込み）のマップ
    aggregationsMap: {
      type: Object,
      default: () => ({}),
    },
    // 集計表ID -> ジョブ状態
    jobStatuses: {
      type: Object,
      default: () => ({}),
    },
    // 集計表ID -> エラーメッセージ
    jobErrorMessages: {
      type: Object,
      default: () => ({}),
    },
    // 集計表ID -> { createdAt, updatedAt }
    jobTimestamps: {
      type: Object,
      default: () => ({}),
    },
    // 集計表ID -> ローディング状態
    loadingStatuses: {
      type: Object,
      default: () => ({}),
    },
    // 全体ローディング
    isLoading: {
      type: Boolean,
      default: false,
    },
    // カテゴリ表示名（ユーザー集計 / 卓集計 / 店舗集計）
    categoryLabel: {
      type: String,
      default: '',
    },
    // シート状態判定関数（集計表ID -> 'error'|'unsaved'|'progress'|'saved'）
    statusResolver: {
      type: Function,
      default: () => 'saved',
    },
    // 集計表ID -> 適用候補ユーザー一覧（適用ユーザーの変更ダイアログ用）
    availableUsers: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const activeIndex = ref(0)

    // 集計表の増減に追従。範囲外になったら先頭に戻す。
    watch(() => props.aggregationsData.length, len => {
      if (activeIndex.value >= len) activeIndex.value = 0
    })

    const activeSheet = computed(() => {
      const data = props.aggregationsData[activeIndex.value]

      return data ? { id: data.attributes.id, name: data.attributes.name } : { id: null, name: '' }
    })

    const activeStatus = computed(() => (
      activeSheet.value.id ? props.statusResolver(activeSheet.value.id) : 'saved'
    ))

    // グリッドが編集中・保存発行待ち（busy）かどうか。AggregationGrid から受け取る。
    const isGridBusy = ref(false)

    // 保存可否。未保存があり、グリッドが編集中・保存発行待ちでなく、
    // セル保存 API 処理中でもないこと。依存関係の再計算が終わる前に
    // 一括保存が走ってデータ不整合になるのを防ぐ。
    const canSave = computed(() => (
      activeStatus.value === 'unsaved'
      && !isGridBusy.value
      && !props.loadingStatuses[activeSheet.value.id]
    ))

    const activeAggregation = computed(() => props.aggregationsMap[activeSheet.value.id] || null)

    const activeErrorMessage = computed(() => props.jobErrorMessages[activeSheet.value.id] || '')

    const activeTimestamp = computed(() => {
      const ts = props.jobTimestamps[activeSheet.value.id]
      if (!ts || !ts.updatedAt) return ''

      try {
        const d = new Date(ts.updatedAt)

        return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} `
          + `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
      } catch (e) {
        return ''
      }
    })

    const unsavedCount = computed(() => (
      props.aggregationsData.reduce((acc, data) => (
        props.statusResolver(data.attributes.id) === 'unsaved' ? acc + 1 : acc
      ), 0)
    ))

    const statusIcon = id => STATUS_ICON[props.statusResolver(id)] || STATUS_ICON.saved
    const statusColor = id => STATUS_COLOR[props.statusResolver(id)] || STATUS_COLOR.saved

    // --- 適用ユーザーの変更 ----------------------------------------------
    // ユーザー集計（EodClosingUserAggregation）のときだけメニュー項目を出す。
    const activeAggregationType = computed(() => {
      const data = props.aggregationsData[activeIndex.value]

      return data?.attributes?.type || ''
    })
    const isUserAggregation = computed(() => (
      activeAggregationType.value === 'EodClosingUserAggregation'
    ))

    const usersDialogOpen = ref(false)

    // アクティブシートの適用済みユーザー（aggregationsData 側に aggregationUsers が載る）
    const activeAggregationUsers = computed(() => {
      const data = props.aggregationsData[activeIndex.value]

      return data?.attributes?.aggregationUsers?.data || []
    })

    // アクティブシートの適用候補ユーザー
    const activeAvailableUsers = computed(() => (
      props.availableUsers[activeSheet.value.id] || []
    ))

    const onFetchAvailableUsers = callback => {
      emit('fetch-available-users', { id: activeSheet.value.id, callback })
    }

    const onCreateUser = ({ userId, callback }) => {
      emit('create-user', { id: activeSheet.value.id, userId, callback })
    }

    const onDestroyUser = ({ id, userId, callback }) => {
      emit('destroy-user', {
        aggregationId: activeSheet.value.id, id, userId, callback,
      })
    }

    // --- 日計ジョブ一括実行 ---------------------------------------------
    // このカテゴリの集計表をまとめて集計（enqueue）する。
    // 実行中はボタンを loading／disabled にする。
    const isBulkEnqueueing = ref(false)

    // カテゴリ内に集計中（progress）の集計表があるか
    const isAnyJobInProgress = computed(() => (
      props.aggregationsData.some(data => (
        props.statusResolver(data.attributes.id) === 'progress'
      ))
    ))

    // 一括実行で実際に enqueue 対象となる集計表が 1 件以上あるか。
    // 判定条件は composable の enqueueAggregationsByType と完全に揃える
    // （jobStatus が null / undefined / 'failed' のものが enqueue 対象）。
    // 全集計表が completed / in_progress なら対象なし＝ボタンを押しても
    // 何も起きないため disabled にする。
    const hasEnqueueableTarget = computed(() => (
      props.aggregationsData.some(data => {
        const status = props.jobStatuses[data.attributes.id]

        return status === null || status === undefined || status === 'failed'
      })
    ))

    // 一括実行可能か。
    // 集計表があり、集計中でなく、一括実行処理中でもなく、
    // かつ enqueue 対象（未実行 or 失敗）が 1 件以上あること。
    const canBulkEnqueue = computed(() => (
      props.aggregationsData.length > 0
      && !isAnyJobInProgress.value
      && !isBulkEnqueueing.value
      && hasEnqueueableTarget.value
    ))

    const onBulkEnqueue = async () => {
      if (!canBulkEnqueue.value) return
      isBulkEnqueueing.value = true
      try {
        // 親（index.vue）でカテゴリに対応する type の一括 enqueue を実行する。
        await new Promise(resolve => {
          emit('bulk-enqueue', { done: resolve })
        })
      } finally {
        isBulkEnqueueing.value = false
      }
    }

    // --- クリップボードにコピー -----------------------------------------
    const gridRef = ref(null)

    const copyToClipboard = async () => {
      // AggregationGrid が組み立てた TSV（ヘッダー＋データ行＋合計行）を取得する
      const text = gridRef.value?.buildClipboardText?.()
      if (!text) {
        vm.$toast.error('コピーできるデータがありません')

        return
      }

      try {
        await navigator.clipboard.writeText(text)
        vm.$toast.info('クリップボードにコピーしました')
      } catch (e) {
        vm.$toast.error('クリップボードへのコピーに失敗しました')
      }
    }

    return {
      activeIndex,
      activeSheet,
      activeStatus,
      isGridBusy,
      canSave,
      activeAggregation,
      activeErrorMessage,
      activeTimestamp,
      unsavedCount,
      statusIcon,
      statusColor,
      isUserAggregation,
      usersDialogOpen,
      activeAggregationUsers,
      activeAvailableUsers,
      onFetchAvailableUsers,
      onCreateUser,
      onDestroyUser,
      isBulkEnqueueing,
      canBulkEnqueue,
      onBulkEnqueue,
      gridRef,
      copyToClipboard,
      icons: {
        mdiReload,
        mdiSync,
        mdiDotsVertical,
        mdiAlert,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
// カテゴリ見出し。一括実行ボタンと縦位置を揃えるため余白をリセットする。
.category-heading {
  margin: 0;
}

.sheet-bar {
  border-bottom: 1px solid rgba(0, 0, 0, .1);
}

.sheet-title {
  font-size: 20px;
}

.sheet-meta {
  font-size: 11px;
  white-space: nowrap;
}

.sheet-tabs {
  border-top: 1px solid rgba(0, 0, 0, .1);
  overflow-x: auto;
  gap: 4px;
}

.sheet-tab {
  display: flex;
  align-items: center;
  white-space: nowrap;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 700;
  border-radius: 6px;
  cursor: pointer;
  background: rgba(0, 0, 0, .04);
  color: inherit;
  opacity: .75;

  &:hover {
    opacity: 1;
  }

  &.active {
    opacity: 1;
    color: #fff;
    background: var(--v-primary-base);
  }
}
</style>
